<script setup lang="ts">
import { Check, ChevronsRight, X } from '@lucide/vue'
import {onMounted, ref} from 'vue';
import {SettingNs} from "@shared/types";
import {ButtonElement} from "@shared/components";
import UpgradeElement from "@shared/components/elements/labels/upgrade.element.vue";
import {getLocalStorage, setLocalStorage} from "@shared/helpers";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useRouter} from 'vue-router';
import {toast} from '@shared/ms-components'

const router = useRouter()
const workspaceStore = useWorkspaceStore();
const emit = defineEmits(['close']);

// Data Properties

// Reference Properties
const notifyRequested = ref(false);

// Computed Properties

// Methods
onMounted(() => {
  notifyRequested.value = getLocalStorage(`notifyRequested.${workspaceStore.getSelectedWorkspaceId}`);
});

const closePopup = () => {
  emit('close')
}

// For later @click="goToUpgradePage"
const goToUpgradePage = () => {
  router.push({
    name: SettingNs.SettingRouteEnums.UPGRADE
  });
}


// Method to handle the notification request
const handleNotifyMe = () => {
  workspaceStore.sendUpgradeRequest({
    workspaceId: workspaceStore.getSelectedWorkspaceId,
    projectId: ''
  }).then(() => {
    notifyRequested.value = true;
    setLocalStorage(`notifyRequested.${workspaceStore.getSelectedWorkspaceId}`, true)

    // Show toaster message
    toast.success('Request completed.', {description: 'A notification will be sent once the upgrade becomes available.'});
  })

};

</script>

<template>
  <div class="upgrade-overlay" tabindex="-1">
    <div class="upgrade-box">
      <div class="upgrade-box__header">
        <div class="upgrade-box__header-title">
          <UpgradeElement size="sm"/>
          <h2>
            Upgrade
          </h2>
        </div>
        <ButtonElement class="btn--ms btn--close" @click="closePopup">
          <X aria-hidden="true" />
        </ButtonElement>
      </div>
      <div class="upgrade-box__body">
        <h3>Unlock Additional  AI Power and save more time </h3>
        <p>
          You've explored the power of metastory AI in the free version - now take your workflow to the next level. Upgrade to metastory AI Pro and unlock enhanced AI capabilities and deeper insights designed for professional Product Owners, Business Analysts, and Requirements Engineers.
        </p>

        <div class="upgrade-box__body-list">
          <p><b>AI Professional</b> – More Methods, Higher Accuracy</p>
          <p><b>QA</b> – Create Acceptance Criteria, and More</p>
          <p><b>Jira Sync</b> – Stay Aligned with Your Development Team</p>
        </div>

      </div>

      <div class="upgrade-box__plans">
        <div class="upgrade-box__plans-plan">
          <h3>Starter</h3>
          <h4>$0 <span>/ month</span></h4>
          <ButtonElement class="btn--ms btn--silent" disabled>
            <Check aria-hidden="true" />
            Selected
          </ButtonElement>
          <p class="info">

          </p>
        </div>
        <div class="upgrade-box__plans-plan upgrade-box__plans-plan--pro">
          <h3>Pro</h3>
          <h4>10$ <span> per user / yearly</span></h4>
          <ButtonElement class="btn--ms btn--accent"  @click="goToUpgradePage">
            <div class="button--switch">
              <ChevronsRight aria-hidden="true" />
              See Plans
            </div>
          </ButtonElement>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

.upgrade-overlay {
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 99999;
  background: var(--upgrade-card-overlay);
  transition: opacity 250ms ease 0s;
}

/* Block */
.upgrade-box {
  background-color: var(--upgrade-card-bg);
  border-radius: $br-md;
  box-shadow: var(--upgrade-card-shadow);
  width: 450px;
  box-sizing: border-box;
  position: fixed;
  bottom: 65px;
  right: 20px;
  z-index: 9;
  animation: dropdownMenuAnimation .06s ease-in;
  overflow: hidden;


  /* Element: Header */
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: var(--upgrade-card-white-bg);
    padding: var(--space-16);
    border-bottom: 1px solid var(--upgrade-card-border);

    &-title {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: var(--space-8);

      h2 {
        font-size: var(--fs-16);
        margin: var(--space-0);
        display: flex;
        align-items: center;
        gap: var(--space-4);
        line-height: normal;

        span {
          font-weight: 400;
          color: var(--text-neutral);
        }
      }
    }
  }

  /* Element: Content */
  &__body {
    padding: var(--space-16) var(--space-16) var(--space-0);
    font-size: var(--fs-14);
    color: var(--text-dark);
    text-align: left;

    h3 {
      font-size: var(--fs-14);
    }

    /* Element: Features List */
    &-features {
      display: flex;
      flex-wrap: wrap;
      list-style-type: none;
      padding: 0;
      margin: var(--space-0);

      li {
        font-size: var(--fs-13);
        flex: 1 1 50%;
        box-sizing: border-box;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: var(--space-6);
        line-height: 2em;
      }
    }

    &-list{
      p{
        margin-bottom: var(--space-8);
      }
    }

  }

  /* Element: Plans */
  &__plans {
    display: flex;
    justify-content: space-between;
    padding: var(--space-16);
    gap: var(--space-8);

    /* Element: Plan */
    &-plan {
      display: flex;
      flex-direction: column;
      flex: 1 1 48%;
      box-sizing: border-box;
      text-align: left;
      padding: var(--space-16);
      border-radius: $br-md;
      background-color: var(--upgrade-card-white-bg);
      border: 1px solid var(--upgrade-card-border);

      &--pro {
        border: 2px solid var(--upgrade-card-border-highlight);
      }

      h3 {
        font-size: var(--fs-18);
        margin-bottom: var(--space-32);
      }

      h4 {
        font-size: var(--fs-24);
        margin-bottom: var(--space-16);

        span {
          font-size: var(--fs-14);
          color: var(--text-neutral);
          font-weight: 400;
        }
      }

      .info {
        font-size: var(--fs-12);
        color: var(--text-neutral);
        margin: var(--space-0);
        margin-top: var(--space-16);
      }
    }
  }

  .button--switch {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-8);
  }
}
</style>
